import { Component, OnInit } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { HourglassService } from 'src/app/services/hourglass.service';
import { AuthService } from 'src/app/services/auth.service';
import * as dayjs from 'dayjs';

@Component({
  selector: 'app-tab-billing-review-grid',
  templateUrl: './tab-billing-review-grid.component.html',
  styleUrls: ['./tab-billing-review-grid.component.css']
})
export class TabBillingReviewGridComponent implements OnInit {
  rowData: any[] = [];
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
    floatingFilter: true
  };
  token: any;
  gridApi: any;
  gridColumnApi: any;
  userMap: { [key: number]: string } = {};
  taskMap: { [key: number]: string } = {};
  taskTypeMap: { [key: number]: string } = {};
  activityLog: { timestamp: string, message: string, success: boolean }[] = [];
  refFieldMaps: { [key: string]: { [key: number]: string } } = {
    task_id: this.taskMap,
    log_type: this.taskTypeMap,
    entry_owner: this.userMap,
    entered_by: this.userMap,
    billable: {
      1: 'Yes',
      0: 'No'
    }
  };
  clientMap: { [key: string]: string } = {};
  constructor(
    private dataService: HourglassService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    const user: any = sessionStorage.getItem("wOSUser");
    if (!user) {
      console.error('User not found in session storage');
      return;
    }
    this.token = JSON.parse(user).stsTokenManager.accessToken;
    this.dataService.getReviewRefCols(this.token)
      .then(refData => {
        refData.users.forEach((u: any) => {
          this.userMap[u.id] = u.name;
        });
        refData.tasks.forEach((t: any) => {
          this.taskMap[t.id] = t.name;
        });
        refData.tasktype.forEach((tt: any) => {
          this.taskTypeMap[tt.id] = tt.name;
        });
        refData.clients.forEach((client: any) => {
          this.clientMap[client.client_id] = client.client_name; // Use the correct field names from the API response
        });
        this.columnDefs = [
          {
            field: 'entry_owner', headerName: 'Resource', editable: false, filter: 'agSetColumnFilter',
            valueFormatter: params => this.userMap[params.value] || params.value,
            filterParams: {
              valueFormatter: (params: any) => this.userMap[params.value] || params.value
            },
            width: 140,
          },
          {
            field: 't_date', headerName: 'Date', filter: 'agDateColumnFilter',
            filterParams: {
              comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
                if (!cellValue) return -1;
                const cellDate = new Date(cellValue);
                filterLocalDateAtMidnight.setHours(0, 0, 0, 0);
                cellDate.setHours(0, 0, 0, 0);
                if (cellDate < filterLocalDateAtMidnight) return -1;
                if (cellDate > filterLocalDateAtMidnight) return 1;
                return 0;
              }
            },
            valueFormatter: (params) => {
              return params.value ? new Date(params.value).toLocaleDateString('en-GB') : '';
            },
            editable: true,
            width: 140,
          },
          {
            field: 'task_id', headerName: 'Task', editable: true, filter: 'agSetColumnFilter', cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
              values: Object.keys(this.taskMap).map(id => Number(id))
            },
            valueFormatter: params => this.taskMap[params.value] || params.value,
            filterParams: {
              valueFormatter: (params: any) => this.taskMap[params.value] || params.value
            },
            keyCreator: params => params.value,
          },
          {
            field: 'client_ids', headerName: 'Clients', editable: false,
            valueFormatter: params => {
              const clientIds = params.value ? params.value.split(',') : [];
              return clientIds
                .map((clientId: string) => this.clientMap[clientId] || clientId)  // Map each clientId to clientName
                .join(', ');  // Join the names with commas
            },
            filter: 'agSetColumnFilter',
            filterParams: {
              // Apply the same valueFormatter for the filter
              valueFormatter: (params: any) => {
                const clientIds = params.value ? params.value.split(',') : [];
                return clientIds
                  .map((clientId: string) => this.clientMap[clientId] || clientId)  // Map each clientId to clientName
                  .join(', ');  // Join the names with commas
              },
              width: 200,
            }
          },
          {
            field: 'duration', headerName: 'Duration', editable: true,
            valueFormatter: this.formatDuration.bind(this),
            width: 100,
          },
          {
            field: 'billable', headerName: 'Billable', editable: true, cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
              values: [1, 0]
            },
            valueFormatter: params => params.value === 1 ? 'Yes' : 'No',
            width: 100,
          },
          { field: 'notes', headerName: 'Notes', editable: true },

          {
            field: 'log_type', headerName: 'Type', editable: true, filter: 'agSetColumnFilter', cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
              values: Object.keys(this.taskTypeMap).map(id => Number(id))
            },
            valueFormatter: params => this.taskTypeMap[params.value] || params.value,
            filterParams: {
              valueFormatter: (params: any) => this.taskTypeMap[params.value] || params.value
            },
            keyCreator: params => params.value
          },
          { field: 't_start', headerName: 'Start', editable: false },
          { field: 't_end', headerName: 'End', editable: false },
          {
            field: 'entered_by', headerName: 'Entered By', editable: false, cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
              values: Object.keys(this.userMap).map(id => Number(id))
            },
            valueFormatter: params => this.userMap[params.value] || params.value,
            width: 140,
          },
          { field: 'created_at', headerName: 'Created At', editable: false },
          
        ];
        this.loadTimeLogs(this.token);
      })
      .catch(err => console.error('Error loading ref data:', err));
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    setTimeout(() => {
      this.gridApi.sizeColumnsToFit();
    }, 200);
  }

  onCellValueChanged(event: any): void {
    const updatedRow = event.data;
    const field = event.colDef.field;
    const oldValue = event.oldValue;
    const newValue = event.newValue;
    const rowId = updatedRow.id;
    if (oldValue === newValue) {
      this.logActivity(`No change detected for ID ${rowId}, field '${field}'`, true);
      return;
    }
    const isRefField = this.refFieldMaps.hasOwnProperty(field);
    const map = this.refFieldMaps[field];
    const oldDisplay = isRefField ? (map[oldValue] || oldValue) : oldValue;
    const newDisplay = isRefField ? (map[newValue] || newValue) : newValue;
    const message = isRefField
      ? `✔ Update success: ID ${rowId}, field '${field}', '${oldValue}' → '${newValue}' ('${oldDisplay}' → '${newDisplay}')`
      : `✔ Update success: ID ${rowId}, field '${field}', '${oldValue}' → '${newValue}'`;
    this.dataService.updateTimeLog(updatedRow, this.token)
      .then(() => this.logActivity(message, true))
      .catch(err => {
        console.error('Error updating time log:', err);
        this.logActivity(message.replace('✔ Update success', '✖ Update FAILED'), false);
      });
  }
  loadTimeLogs(token: string): void {
    console.log('loadTimeLogs Start', dayjs(Date.now()).format("HH:mm:ss"))
    this.dataService.getAllTaskTimeLogs(token)
      .then(data => {
        console.log('loadTimeLogs End', dayjs(Date.now()).format("HH:mm:ss"))
        this.rowData = data;

      })
      .catch(err => console.error('Error fetching time logs:', err));
  }

  formatDuration(params: any): string {
    const totalMinutes = Number(params.value);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  formatDate(params: any): string {
    const raw = params.value;
    if (!raw) return '';
    return dayjs(raw).format('DD/MM/YYYY');
  }

  logActivity(message: string, success: boolean): void {
    const timestamp = new Date().toLocaleTimeString();
    this.activityLog.unshift({ timestamp, message, success });
    // Optional: cap log size
    if (this.activityLog.length > 100) this.activityLog.pop();
  }
}
